/**
 * Analysis Dashboard Component
 * Comprehensive view of all analysis runs with statistics, filters, and quick actions
 */

import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { analysisAPI } from "../../utils/analysisAPI";
import { useAuth } from "../../context/AuthContext";
import { showToast } from "../../components/Toast";
import { showConfirm } from "../../components/ConfirmModal";
import MetricCard from "../../components/MetricCard";
import ModelCard from "../../components/ModelCard";
import {
  FiHash,
  FiCheckCircle,
  FiActivity,
  FiFileText,
  FiAlertTriangle,
} from "react-icons/fi";
import "./AnalysisDashboard.css";

const AnalysisDashboard = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const effectiveOrgId = useMemo(() => {
    if (orgId) return Number(orgId);
    if (user?.organization && typeof user.organization === "object") {
      return user.organization.id;
    }
    return user?.organization || null;
  }, [orgId, user]);

  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    model_category: "",
    status: "",
  });

  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    running: 0,
    draft: 0,
    failed: 0,
    pending: 0,
  });

  useEffect(() => {
    loadAnalyses();
  }, [filters, effectiveOrgId]);

  // Auto-poll every 10 s while any analysis is RUNNING
  useEffect(() => {
    const hasRunning = analyses.some((a) => a.status === "RUNNING");
    if (!hasRunning) return;
    const timer = setInterval(() => {
      loadAnalyses();
    }, 10000);
    return () => clearInterval(timer);
  }, [analyses]);

  const loadAnalyses = async () => {
    try {
      setLoading(true);
      const result = await analysisAPI.listAnalyses(effectiveOrgId || null, filters);
      const analysesList = result?.list ?? (Array.isArray(result) ? result : result?.results ?? []);
      const counts = result?.counts;

      setAnalyses(analysesList);
      if (counts) {
        setStats({
          total: counts.total ?? analysesList.length,
          completed: counts.completed ?? 0,
          running: counts.running ?? 0,
          draft: counts.draft ?? 0,
          failed: counts.failed ?? 0,
          pending: counts.pending ?? 0,
        });
      } else {
        setStats({
          total: analysesList.length,
          completed: analysesList.filter((a) => a.status === "COMPLETED").length,
          running: analysesList.filter((a) => a.status === "RUNNING").length,
          draft: analysesList.filter((a) => a.status === "DRAFT").length,
          failed: analysesList.filter((a) => a.status === "FAILED").length,
          pending: analysesList.filter((a) => a.status === "PENDING").length,
        });
      }
      setError(null);
    } catch (err) {
      setError(err.message);
      setAnalyses([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();

    const confirmed = await showConfirm({
      title: "Delete Analysis?",
      message:
        "This action cannot be undone. The analysis and all its data will be permanently deleted.",
      confirmText: "Delete",
      cancelText: "Cancel",
      type: "danger",
    });

    if (!confirmed) return;

    try {
      await analysisAPI.deleteAnalysis(id);
      showToast.success("Analysis deleted successfully");
      loadAnalyses();
    } catch (err) {
      showToast.error("Failed to delete analysis: " + err.message);
    }
  };

  const getStatusBadge = (status) => {
    const badgeMap = {
      DRAFT: { class: "badge-draft", icon: "", label: "Draft" },
      PENDING: { class: "badge-pending", icon: "", label: "Pending" },
      RUNNING: { class: "badge-running", icon: "", label: "Running" },
      COMPLETED: { class: "badge-completed", icon: "\u2713", label: "Completed" },
      FAILED: { class: "badge-failed", icon: "\u2717", label: "Failed" },
    };
    const badge = badgeMap[status] || badgeMap.DRAFT;
    return badge;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getCategoryIcon = (category) => {
    return "";
  };

  return (
    <div className="analysis-dashboard">
      {/* Header Section */}
      <div className="dashboard-header">
        <div className="header-content">
          <div>
            <h1>Analysis & Models</h1>
            <p className="subtitle">
              Create and manage ML model analyses on your datasets
            </p>
          </div>
          <button
            className="btn btn-primary btn-lg"
            onClick={() => navigate("/analyst/analysis/new")}
          >
            + New Analysis
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="stats-section">
        <MetricCard
          label="Total Analyses"
          value={stats.total}
          tone="primary"
          size="lg"
          icon={<FiHash />}
        />
        <MetricCard
          label="Completed"
          value={stats.completed}
          tone="success"
          icon={<FiCheckCircle />}
        />
        <MetricCard
          label="Running"
          value={stats.running}
          tone="warning"
          icon={<FiActivity />}
        />
        <MetricCard
          label="Drafts"
          value={stats.draft}
          tone="default"
          icon={<FiFileText />}
        />
        <MetricCard
          label="Failed"
          value={stats.failed}
          tone="danger"
          icon={<FiAlertTriangle />}
        />
      </div>

      {/* Filters Section */}
      <div className="filters-section">
        <h3>Filters</h3>
        <div className="filters-grid">
          <div className="filter-group">
            <label>Search</label>
            <input
              type="text"
              placeholder="Dataset, model name, category..."
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <label>Model Type</label>
            <select
              value={filters.model_category}
              onChange={(e) =>
                handleFilterChange("model_category", e.target.value)
              }
              className="filter-select"
            >
              <option value="">All Models</option>
              <option value="REGRESSION">Regression</option>
              <option value="CLASSIFICATION">Classification</option>
              <option value="CLUSTERING">Clustering</option>
              <option value="TIME_SERIES">Time Series</option>
              <option value="ANOMALY_DETECTION">Anomaly Detection</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              className="filter-select"
            >
              <option value="">All Statuses</option>
              <option value="DRAFT">Draft</option>
              <option value="PENDING">Pending</option>
              <option value="RUNNING">Running</option>
              <option value="COMPLETED">Completed</option>
              <option value="FAILED">Failed</option>
            </select>
          </div>

          <button
            className="btn btn-secondary"
            onClick={() =>
              setFilters({ dataset: "", model_category: "", status: "" })
            }
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Content Section */}
      {error && (
        <div className="alert alert-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading analyses...</p>
        </div>
      ) : analyses.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">—</div>
          <h3>No analyses found</h3>
          <p>
            {Object.values(filters).some((f) => f)
              ? "Try adjusting your filters"
              : "Create your first analysis to get started!"}
          </p>
          <button
            className="btn btn-primary"
            onClick={() => navigate("/analyst/analysis/new")}
          >
            Create Your First Analysis
          </button>
        </div>
      ) : (
        <div className="analyses-grid">
          {analyses.map((analysis) => (
            <ModelCard
              key={analysis.id}
              title={analysis.model_name}
              category={analysis.model_category}
              status={analysis.status}
              datasetLabel={analysis.data_table || analysis.dataset || "—"}
              clientName={analysis.client_name}
              createdAt={analysis.created_at}
              accuracy={analysis.results && analysis.results.accuracy}
              durationMinutes={
                analysis.execution_time_seconds
                  ? analysis.execution_time_seconds / 60
                  : null
              }
              onClick={() => navigate(`/analyst/analysis/${analysis.id}`)}
              onViewDetails={() =>
                navigate(`/analyst/analysis/${analysis.id}`)
              }
              onDelete={() => handleDelete(analysis.id, { stopPropagation: () => {} })}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AnalysisDashboard;
